import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Mag } from '../components/mag.js';
import type { CampaignDetail } from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { campaignQuery, meQuery } from '../lib/queries.js';

// LAUNCHED — one-shot success / celebration page (s135). Founders see
// this once per campaign per device, right after the review handoff.
// On subsequent visits the route's beforeLoad reads the
// `mag.celebrated.<id>` localStorage marker and redirects to the
// canonical campaign-home at /brand/$slug/campaigns/$id. The component
// sets the marker on mount, so the next visit lands on details
// instead of replaying the hero.
//
// Until real ad-platform shipping lands, "live" is cosmetic — Mag has
// a saved plan but nothing's running on Meta or Google yet.

const CELEBRATED_KEY_PREFIX = 'mag.celebrated.';

function hasCelebrated(campaignId: string): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(`${CELEBRATED_KEY_PREFIX}${campaignId}`) === 'true';
  } catch {
    // localStorage refused (private browsing, etc.) — treat as
    // never-celebrated. Worst case: founder sees the hero a few extra
    // times until they switch contexts. No data loss.
    return false;
  }
}

function markCelebrated(campaignId: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(`${CELEBRATED_KEY_PREFIX}${campaignId}`, 'true');
  } catch {
    /* swallow — see above */
  }
}

export const Route = createFileRoute('/brand/$slug/campaigns/new/launched/$id')({
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const campaign = await queryClient.ensureQueryData(campaignQuery(params.id));
    if (!campaign) throw redirect({ to: '/brands' });
    if (campaign.status === 'drafting') {
      throw redirect({
        to: '/brand/$slug/campaigns/new/drafting/$id',
        params: { slug: params.slug, id: campaign.id },
      });
    }
    if (campaign.status === 'ready') {
      throw redirect({
        to: '/brand/$slug/campaigns/new/review/$id',
        params: { slug: params.slug, id: campaign.id },
      });
    }
    // One-shot celebration (s135) — if the founder has already seen
    // this hero for this campaign, send them to the working details
    // page. The marker lives in localStorage so the celebration
    // doesn't replay across reloads or tabs on the same device.
    if (hasCelebrated(campaign.id)) {
      throw redirect({
        to: '/brand/$slug/campaigns/$id',
        params: { slug: params.slug, id: campaign.id },
      });
    }
    return { me, campaign };
  },
  loader: ({ context }) => context as { me: unknown; campaign: CampaignDetail },
  component: LaunchedPage,
});

function LaunchedPage() {
  const { campaign } = Route.useLoaderData();
  const { slug } = Route.useParams();
  const { i18n, t } = useLingui();
  useBrandTitle(slug, [campaign.name ?? t`campaign`]);
  const shortId = campaign.id.replace(/-/g, '').slice(0, 5);
  const cmpId = `cmp_${shortId}`;

  // Set the celebrated marker on mount so the next visit redirects
  // to the details page instead of re-rendering the hero. Done in an
  // effect (not in render) so it only fires once per mount and not
  // during a redirect.
  useEffect(() => {
    markCelebrated(campaign.id);
  }, [campaign.id]);

  const isAnalytics = campaign.kind === 'analytics_setup';
  const pitch = launchedPitch(campaign, i18n);

  return (
    <main className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[var(--spell)] px-4 text-center sm:px-7">
      <Mag size={140} mood="smug" />
      <p className="mt-6 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/60">
        {'↘'} <Trans>LIVE · MAG IS ON IT</Trans>
      </p>
      <h1 className="mt-3 font-display text-[44px] sm:text-[56px] font-black leading-[1] tracking-[-0.045em] text-[var(--ink)]">
        <Trans>shipped.</Trans>
      </h1>
      <p className="mt-4 max-w-[520px] font-serif text-[18px] sm:text-[22px] leading-tight text-[var(--ink)]/85">
        <Trans>“{pitch}”</Trans>
      </p>
      {/* Threshold-moment cat-ism per spec §1. */}
      <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--ink)]/45">
        {'↘'} <Trans>*tail flicks*</Trans>
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <Link to="/brand/$slug/home" params={{ slug }} className="btn">
          <Trans>back to home</Trans>
          <span className="btn-arrow">→</span>
        </Link>
        <Link to="/brand/$slug/campaigns/new/brief" params={{ slug }} className="btn btn-ghost">
          <Trans>brief another</Trans>
          <span className="btn-arrow">→</span>
        </Link>
        {isAnalytics ? (
          // s135 — nav to the new details route (not an in-page
          // anchor). This is where the campaign actually lives from
          // now on.
          <Link
            to="/brand/$slug/campaigns/$id"
            params={{ slug, id: campaign.id }}
            className="btn btn-ghost"
          >
            <Trans>ongoing reads</Trans>
            <span className="btn-arrow">→</span>
          </Link>
        ) : null}
      </div>
      <p className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--ink)]/55">
        {'↘'} <Trans>campaign id · {cmpId}</Trans>
      </p>
    </main>
  );
}

/** Mag's closing line on the launched page — pulled from whatever the
 *  draft surfaces as its "after-launch" hook. Plans (s181+) prefer
 *  postInstallCheck when present, then timeline. Legacy
 *  analytics_setup carries postInstallCheck on the draft itself;
 *  legacy creative_ads carries timeline. */
function launchedPitch(campaign: CampaignDetail, i18n: I18n): string {
  // Two messages, not one with a conditional prefix: `timeline` is
  // LLM-written prose in the founder's own language, and a translator
  // needs to be free to move the follow-up sentence around it.
  const goingLive = () =>
    i18n._(msg`going live tomorrow morning. i'll send you the first read by friday.`);
  const afterTimeline = (timeline: string) =>
    i18n._(msg`${timeline} i'll send you the first read by friday.`);
  const draft = campaign.draft;
  if (!draft) {
    return goingLive();
  }
  if ('shape' in draft && draft.shape === 'plan') {
    if (draft.postInstallCheck) return draft.postInstallCheck;
    if (draft.timeline) return afterTimeline(draft.timeline);
    return goingLive();
  }
  if ('kind' in draft && draft.kind === 'analytics_setup') {
    return draft.postInstallCheck;
  }
  if ('timeline' in draft && draft.timeline) {
    return afterTimeline(draft.timeline);
  }
  return goingLive();
}
